import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { EMPTY_VALUE, REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { ArcButton, ArcDialog, ArcOutlinedInput, ArcStack, ArcText } from '@src/core/ui';
import { TwoFactorCodeDialogProps } from './types';

interface ICodeForm {
  code: string;
}

// Reusable confirmation dialog: the backend requires a current TOTP (or backup) code to
// disable 2FA or regenerate backup codes. The form is reset whenever the dialog reopens so a
// stale code is never resubmitted.
const TwoFactorCodeDialog: React.FC<TwoFactorCodeDialogProps> = (props) => {
  const { t } = useTranslation();
  const {
    open,
    onClose,
    onSubmit,
    isSubmitting,
    errorMessage,
    title,
    description,
    submitText,
  } = props;

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ICodeForm>({
    defaultValues: { code: EMPTY_VALUE },
  });

  React.useEffect(() => {
    if (open) {
      reset({ code: EMPTY_VALUE });
    }
  }, [open, reset]);

  return (
    <ArcDialog open={open} onClose={onClose} title={title}>
      <ArcStack
        p={2}
        spacing={2}
        component='form'
        onSubmit={handleSubmit((data) => onSubmit(data.code.trim()))}
      >
        <ArcText fontSize={14} color='text.secondary'>
          {description}
        </ArcText>

        <ArcOutlinedInput
          autoFocus
          autoComplete='one-time-code'
          label={t('mfa.profile.codeLabel')}
          error={Boolean(errors.code) || Boolean(errorMessage)}
          helperText={errors.code?.message || errorMessage || ''}
          {...register('code', { required: REQUIRED_VALIDATION_TEXT })}
        />

        <ArcButton.brand type='submit' isLoading={isSubmitting}>
          {submitText}
        </ArcButton.brand>
      </ArcStack>
    </ArcDialog>
  );
};

export default TwoFactorCodeDialog;
